import { useState, type FormEvent, type WheelEvent } from 'react';
import type {
  Cliente,
  FormaPagoPaquete,
  GuiaPdfItemViaje,
  Paquete,
  PagoPaquete,
  Sucursal,
  TipoCliente,
  TipoPagoParcial,
  Viaje,
} from '../../lib/appTypes';
import { saveViaje } from '../../lib/storage';
import { montoPorCobrarDe, pagoParaGuardar, validarMontoPorCobrar } from '../../lib/pagos';

type EditableGuiaProcesada = {
  url: string;
  nombreArchivo: string;
  numeroFactura: string;
  direccion: string;
  total?: number;
  items: GuiaPdfItemViaje[];
};

type Props = {
  initialViaje: Viaje;
  sucursales: Sucursal[];
  onSaved: (viaje: Viaje) => void | Promise<void>;
  onCancel: () => void;
};

const PAYMENT_TYPE_OPTIONS: Array<{ value: FormaPagoPaquete; label: string }> = [
  { value: '', label: 'Selecciona una opción' },
  { value: 'parcial', label: 'Por cobrar' },
  { value: 'completo', label: 'Pagado' },
];

/** Formas de pago antiguas: ya no se ofrecen, pero se muestran si la entrega las tiene. */
const LEGACY_PAYMENT_LABELS: Partial<Record<FormaPagoPaquete, string>> = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  cheque: 'Cheque',
  nota_credito: 'Nota de crédito',
};

const PARTIAL_PAYMENT_OPTIONS: Array<{ value: TipoPagoParcial; label: string }> = [
  { value: '', label: 'Selecciona una opción' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'nota_credito', label: 'Nota de crédito' },
];

function createEmptyPago(): PagoPaquete {
  return { tipo: '' };
}

function createEmptyCliente(): Cliente {
  return {
    nombre: '',
    numero: '',
    fechaDocumento: '',
    ciudad: '',
    sucursal: '',
    correoElectronico: '',
    direccion: '',
    telefono: '',
  };
}

function clonePago(pago?: PagoPaquete): PagoPaquete {
  return pago ? { ...pago } : createEmptyPago();
}

function cloneCliente(cliente?: Cliente): Cliente {
  return cliente ? { ...cliente } : createEmptyCliente();
}

function clonePaquete(paquete?: Paquete): Paquete {
  return {
    nombre: paquete?.nombre ?? '',
    descripcion: paquete?.descripcion ?? '',
    imagenes: [...(paquete?.imagenes ?? [])],
    cliente: cloneCliente(paquete?.cliente),
    guiaPdfUrl: paquete?.guiaPdfUrl ?? '',
    guiaPdfNombreArchivo: paquete?.guiaPdfNombreArchivo ?? '',
    envioPdfUrl: paquete?.envioPdfUrl ?? '',
    envioPdfNombreArchivo: paquete?.envioPdfNombreArchivo ?? '',
    guiasPdf: [...(paquete?.guiasPdf ?? [])],
    valorTotalFactura: paquete?.valorTotalFactura,
    pago: clonePago(paquete?.pago),
  };
}

function createPagoForType(tipo: FormaPagoPaquete, total?: number): PagoPaquete {
  if (tipo === 'completo') {
    return { tipo, monto: total, medioPago: '' };
  }
  if (tipo === 'efectivo' || tipo === 'cheque') {
    return { tipo, monto: total };
  }
  if (tipo === 'nota_credito') {
    return { tipo, valorNotaCredito: total };
  }
  if (tipo === 'parcial') {
    return { tipo, tipoPagoParcial: '', montoPagado: 0 };
  }
  return { tipo };
}

function parseOptionalNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function preventNumberInputWheelChange(event: WheelEvent<HTMLInputElement>): void {
  event.currentTarget.blur();
}

function validatePagoPaquete(pago?: PagoPaquete): string | null {
  if (!pago?.tipo) return 'Selecciona una forma de pago para el paquete.';

  if ((pago.tipo === 'efectivo' || pago.tipo === 'completo') && typeof pago.monto !== 'number') {
    return 'Debes ingresar el monto para la forma de pago seleccionada.';
  }
  if (pago.tipo === 'completo') {
    if (!pago.medioPago) return 'Selecciona cómo fue pagado.';
    if (pago.medioPago === 'transferencia' && (!pago.numeroTransferencia?.trim() || !pago.banco?.trim())) {
      return 'El pago por transferencia requiere número de transferencia y banco.';
    }
    if (pago.medioPago === 'cheque' && (!pago.numeroCheque?.trim() || !pago.banco?.trim())) {
      return 'El pago con cheque requiere número de cheque y banco.';
    }
    if (pago.medioPago === 'nota_credito' && !pago.numeroNotaCredito?.trim()) {
      return 'El pago con nota de crédito requiere el número de la nota.';
    }
  }
  if (pago.tipo === 'transferencia' && (!pago.numeroTransferencia?.trim() || !pago.banco?.trim())) {
    return 'La transferencia requiere número de transferencia y banco.';
  }
  if (pago.tipo === 'cheque' && (!pago.numeroCheque?.trim() || !pago.banco?.trim() || typeof pago.monto !== 'number')) {
    return 'El cheque requiere número, banco y monto.';
  }
  if (pago.tipo === 'nota_credito' && (!pago.numeroNotaCredito?.trim() || typeof pago.valorNotaCredito !== 'number')) {
    return 'La nota de crédito requiere número y valor.';
  }
  if (pago.tipo === 'parcial' && !pago.tipoPagoParcial) return 'Selecciona la forma de cobro sugerida.';

  return null;
}

function buildGuideItemsText(items: GuiaPdfItemViaje[]): string {
  return items.map(item => [
    item.codigo || '',
    item.descripcion || '',
    typeof item.cantidad === 'number' ? String(item.cantidad) : '',
    typeof item.precioUnitario === 'number' ? String(item.precioUnitario) : '',
    typeof item.precioTotal === 'number' ? String(item.precioTotal) : '',
  ].join(' | ')).join('\n');
}

function parseGuideItemsText(value: string): GuiaPdfItemViaje[] {
  return value
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const [codigo = '', descripcion = '', cantidad = '', precioUnitario = '', precioTotal = ''] = line.split('|').map(part => part.trim());
      const parsedCantidad = Number(cantidad);
      const parsedPrecioUnitario = Number(precioUnitario);
      const parsedPrecioTotal = Number(precioTotal);
      return {
        codigo,
        descripcion,
        cantidad: Number.isFinite(parsedCantidad) ? parsedCantidad : undefined,
        precioUnitario: Number.isFinite(parsedPrecioUnitario) ? parsedPrecioUnitario : undefined,
        precioTotal: Number.isFinite(parsedPrecioTotal) ? parsedPrecioTotal : undefined,
      };
    });
}

function formatTipoEntrega(tipo?: Viaje['tipoEntrega']): string {
  if (tipo === 'entrega') return 'Entrega';
  if (tipo === 'envio') return 'Envío';
  if (tipo === 'transferencia') return 'Transferencia';
  return '—';
}

function buildEditableGuias(viaje: Viaje): EditableGuiaProcesada[] {
  const cliente = cloneCliente(viaje.paquete?.cliente);
  const existingGuias = viaje.paquete?.guiasPdf ?? [];

  if (existingGuias.length > 0) {
    return existingGuias.map((guia, index) => ({
      url: guia.url,
      nombreArchivo: guia.nombreArchivo ?? `guia-${index + 1}.pdf`,
      numeroFactura: guia.numeroFactura ?? cliente.numero ?? '',
      direccion: guia.direccion ?? cliente.direccion ?? '',
      total: guia.extraccion?.total ?? guia.valorTotal,
      items: [...(guia.extraccion?.items ?? [])],
    }));
  }

  if (viaje.paquete?.guiaPdfUrl) {
    return [{
      url: viaje.paquete.guiaPdfUrl,
      nombreArchivo: viaje.paquete.guiaPdfNombreArchivo ?? 'guia.pdf',
      numeroFactura: cliente.numero ?? '',
      direccion: cliente.direccion ?? '',
      total: viaje.paquete.valorTotalFactura,
      items: [],
    }];
  }

  return [];
}

export default function AdminEditViajeForm({ initialViaje, sucursales, onSaved, onCancel }: Props) {
  const tipoEntrega = initialViaje.tipoEntrega ?? 'entrega';
  const isTransferencia = tipoEntrega === 'transferencia';
  const [paquete, setPaquete] = useState<Paquete>(() => clonePaquete(initialViaje.paquete));
  const [editableGuias, setEditableGuias] = useState<EditableGuiaProcesada[]>(() => buildEditableGuias(initialViaje));
  const [urlDestino, setUrlDestino] = useState(isTransferencia ? '' : (initialViaje.urlDestino ?? ''));
  const [puntosIntermediosText, setPuntosIntermediosText] = useState(isTransferencia ? '' : (initialViaje.puntosIntermedios ?? []).map(point => point.urlDestino).join('\n'));
  const [tiempoEstimadoMinutos, setTiempoEstimadoMinutos] = useState(isTransferencia ? '' : (initialViaje.tiempoEstimadoMinutos ? String(initialViaje.tiempoEstimadoMinutos) : ''));
  const [companiaTransporte, setCompaniaTransporte] = useState(initialViaje.companiaTransporte ?? '');
  const [valorTotalEnvio, setValorTotalEnvio] = useState(initialViaje.valorTotalEnvio ? String(initialViaje.valorTotalEnvio) : '');
  const [feeEnvio, setFeeEnvio] = useState(initialViaje.feeEnvio ? String(initialViaje.feeEnvio) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const pagoSeleccionado = clonePago(paquete.pago);

  function handlePaqueteField<K extends keyof Paquete>(field: K, value: Paquete[K]) {
    setPaquete(prev => ({ ...prev, [field]: value }));
  }

  function handleClienteField<K extends keyof Cliente>(field: K, value: Cliente[K]) {
    setPaquete(prev => ({
      ...prev,
      cliente: {
        ...cloneCliente(prev.cliente),
        [field]: value,
      },
    }));
  }

  function handlePagoField<K extends keyof PagoPaquete>(field: K, value: PagoPaquete[K]) {
    setPaquete(prev => ({
      ...prev,
      pago: {
        ...clonePago(prev.pago),
        [field]: value,
      },
    }));
  }

  function handleTipoPagoChange(value: FormaPagoPaquete) {
    setPaquete(prev => ({
      ...prev,
      pago: createPagoForType(value, prev.valorTotalFactura),
    }));
  }

  function handleMedioPagoChange(value: TipoPagoParcial) {
    setPaquete(prev => ({
      ...prev,
      pago: {
        ...(prev.pago ?? createPagoForType('completo', prev.valorTotalFactura)),
        tipo: 'completo',
        medioPago: value,
        banco: undefined,
        numeroTransferencia: undefined,
        numeroCheque: undefined,
        numeroNotaCredito: undefined,
      },
    }));
  }

  function handleTipoPagoParcialChange(value: TipoPagoParcial) {
    setPaquete(prev => ({
      ...prev,
      pago: {
        ...(prev.pago ?? createPagoForType('parcial', prev.valorTotalFactura)),
        tipo: 'parcial',
        tipoPagoParcial: value,
        banco: undefined,
        numeroTransferencia: undefined,
        numeroCheque: undefined,
        numeroNotaCredito: undefined,
      },
    }));
  }

  function handleEditableGuiaField(index: number, patch: Partial<EditableGuiaProcesada>) {
    setEditableGuias(prev => prev.map((guia, guiaIndex) => (guiaIndex === index ? { ...guia, ...patch } : guia)));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const cliente = cloneCliente(paquete.cliente);
    const tiempoEstimado = isTransferencia ? undefined : Number(tiempoEstimadoMinutos);
    const valorEnvio = parseOptionalNumber(valorTotalEnvio);
    const fee = parseOptionalNumber(feeEnvio);
    const puntosIntermedios = isTransferencia
      ? []
      : puntosIntermediosText
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
        .map(url => ({ urlDestino: url }));
    if (!paquete.nombre.trim()) {
      setError('Ingresa el nombre de la entrega.');
      return;
    }
    if (!isTransferencia && !urlDestino.trim()) {
      setError('Ingresa la URL de Google Maps del destino.');
      return;
    }
    if (!isTransferencia && (!Number.isFinite(tiempoEstimado) || (tiempoEstimado ?? 0) <= 0)) {
      setError('Ingresa un tiempo estimado válido.');
      return;
    }

    if (tipoEntrega !== 'transferencia') {
      if (!cliente.nombre.trim() || !cliente.numero?.trim() || !cliente.fechaDocumento?.trim() || !cliente.ciudad?.trim() || !cliente.sucursal?.trim() || !cliente.correoElectronico?.trim()) {
        setError('Completa todos los campos del cliente antes de guardar.');
        return;
      }
      const pagoError = validatePagoPaquete(pagoSeleccionado) ?? validarMontoPorCobrar(pagoSeleccionado, paquete.valorTotalFactura);
      if (pagoError) {
        setError(pagoError);
        return;
      }
    }

    if (tipoEntrega === 'envio') {
      if (!companiaTransporte.trim()) {
        setError('Ingresa la compañía de transporte.');
        return;
      }
      if (typeof valorEnvio !== 'number' || valorEnvio < 0) {
        setError('Ingresa un valor total de envío válido.');
        return;
      }
    }

    setSaving(true);
    setError('');
    try {
      const viajeActualizado = await saveViaje({
        ...initialViaje,
        companiaTransporte: tipoEntrega === 'envio' ? companiaTransporte.trim() : initialViaje.companiaTransporte,
        valorTotalEnvio: tipoEntrega === 'envio' ? valorEnvio : initialViaje.valorTotalEnvio,
        feeEnvio: fee,
        urlDestino: isTransferencia ? undefined : urlDestino.trim(),
        puntosIntermedios,
        tiempoEstimadoMinutos: tiempoEstimado,
        // Los datos procesados de la transferencia no se editan aquí (solo nombre y descripción).
        transferencia: initialViaje.transferencia,
        paquete: {
          ...paquete,
          nombre: paquete.nombre.trim(),
          descripcion: paquete.descripcion.trim(),
          valorTotalFactura: paquete.valorTotalFactura,
          pago: tipoEntrega === 'transferencia' ? createEmptyPago() : pagoParaGuardar(pagoSeleccionado, paquete.valorTotalFactura),
          cliente: tipoEntrega === 'transferencia'
            ? {
                ...cliente,
                sucursal: initialViaje.transferencia?.bodegaOrigen || cliente.sucursal,
                correoElectronico: cliente.correoElectronico || 'example@gmail.com',
              }
            : cliente,
          guiaPdfUrl: initialViaje.paquete?.guiaPdfUrl,
          guiaPdfNombreArchivo: initialViaje.paquete?.guiaPdfNombreArchivo,
          guiasPdf: [...(initialViaje.paquete?.guiasPdf ?? [])],
        },
      }, {
        fullEdit: true,
      });

      await onSaved(viajeActualizado);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? 'No se pudo guardar la entrega.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
    <form className="space-y-6" onSubmit={handleSubmit}>
      <div>
        <h3 className="text-xl font-semibold text-stone-950">Editar entrega</h3>
        <p className="text-sm text-stone-500">
          {isTransferencia
            ? 'En una transferencia solo se editan el nombre y la descripción. Los datos del documento y las guías generadas permanecen bloqueados.'
            : 'Solo se modifican los datos creados en la entrega. El tipo de entrega y las guías generadas permanecen bloqueadas.'}
        </p>
      </div>

      {error && <div className="rounded-[var(--radius-panel)] border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-2 text-sm text-stone-700">
          <span className="font-medium">Nombre de la entrega</span>
          <input value={paquete.nombre} onChange={event => handlePaqueteField('nombre', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" />
        </label>
        <div className="space-y-2 text-sm text-stone-700">
          <span className="font-medium">Tipo de entrega</span>
          <div className="rounded-[14px] border border-stone-200 bg-stone-50 px-3 py-2.5 text-stone-700">{formatTipoEntrega(tipoEntrega)}</div>
        </div>
      </div>

      <label className="space-y-2 text-sm text-stone-700">
        <span className="font-medium">Descripción</span>
        <textarea value={paquete.descripcion} onChange={event => handlePaqueteField('descripcion', event.target.value)} rows={3} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" />
      </label>

      {!isTransferencia && (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm text-stone-700">
              <span className="font-medium">URL de destino</span>
              <input value={urlDestino} onChange={event => setUrlDestino(event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" />
            </label>
            <label className="space-y-2 text-sm text-stone-700">
              <span className="font-medium">Tiempo estimado (minutos)</span>
              <input type="number" min="1" value={tiempoEstimadoMinutos} onWheel={preventNumberInputWheelChange} onChange={event => setTiempoEstimadoMinutos(event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" />
            </label>
          </div>

          <label className="space-y-2 text-sm text-stone-700">
            <span className="font-medium">Puntos intermedios</span>
            <textarea value={puntosIntermediosText} onChange={event => setPuntosIntermediosText(event.target.value)} rows={3} placeholder="Una URL por línea" className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" />
          </label>
        </>
      )}

      {tipoEntrega === 'envio' && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <label className="space-y-2 text-sm text-stone-700 md:col-span-2">
              <span className="font-medium">Compañía de transporte</span>
              <input value={companiaTransporte} onChange={event => setCompaniaTransporte(event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" />
            </label>
            <label className="space-y-2 text-sm text-stone-700">
              <span className="font-medium">Valor total de envío</span>
              <input type="number" min="0" step="0.01" value={valorTotalEnvio} onWheel={preventNumberInputWheelChange} onChange={event => setValorTotalEnvio(event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" />
            </label>
          </div>
          <label className="space-y-2 text-sm text-stone-700">
            <span className="font-medium">Costo adicional de envío</span>
            <input type="number" min="0" step="0.01" value={feeEnvio} onWheel={preventNumberInputWheelChange} onChange={event => setFeeEnvio(event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" />
          </label>
        </>
      )}

      {tipoEntrega !== 'transferencia' ? (
        <>
          <div className="space-y-4 rounded-[var(--radius-card)] border border-stone-200 p-4">
            <h4 className="font-semibold text-stone-900">Cliente</h4>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Nombre</span><input value={paquete.cliente?.nombre ?? ''} onChange={event => handleClienteField('nombre', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" /></label>
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Número</span><input value={paquete.cliente?.numero ?? ''} onChange={event => handleClienteField('numero', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" /></label>
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Fecha documento</span><input value={paquete.cliente?.fechaDocumento ?? ''} onChange={event => handleClienteField('fechaDocumento', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" /></label>
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Ciudad</span><input value={paquete.cliente?.ciudad ?? ''} onChange={event => handleClienteField('ciudad', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" /></label>
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Sucursal</span><input value={paquete.cliente?.sucursal ?? ''} onChange={event => handleClienteField('sucursal', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" /></label>
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Correo</span><input value={paquete.cliente?.correoElectronico ?? ''} onChange={event => handleClienteField('correoElectronico', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" /></label>
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Teléfono</span><input value={paquete.cliente?.telefono ?? ''} onChange={event => handleClienteField('telefono', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" /></label>
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Dirección</span><input value={paquete.cliente?.direccion ?? ''} onChange={event => handleClienteField('direccion', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" /></label>
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Tipo de cliente</span><select value={paquete.cliente?.tipoCliente || 'cliente_final'} onChange={event => handleClienteField('tipoCliente', event.target.value as TipoCliente)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10"><option value="cliente_final">Cliente final</option><option value="aliado_estrategico">Aliado estratégico</option></select></label>
            </div>
          </div>

          <div className="space-y-4 rounded-[var(--radius-card)] border border-stone-200 p-4">
            <h4 className="font-semibold text-stone-900">Forma de pago</h4>
            <p className="text-sm text-stone-500">Puedes editar la forma de pago si lo necesitas. Si no cambias nada, se envía la que ya estaba cargada en la entrega.</p>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Tipo</span><select value={pagoSeleccionado.tipo ?? ''} onChange={event => handleTipoPagoChange(event.target.value as FormaPagoPaquete)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10">{PAYMENT_TYPE_OPTIONS.map(option => <option key={option.value || 'blank'} value={option.value}>{option.label}</option>)}{pagoSeleccionado.tipo && LEGACY_PAYMENT_LABELS[pagoSeleccionado.tipo] && <option value={pagoSeleccionado.tipo}>{LEGACY_PAYMENT_LABELS[pagoSeleccionado.tipo]} (forma anterior)</option>}</select></label>
              <div className="space-y-2 text-sm text-stone-700"><span className="font-medium">Valor total factura</span><div className="rounded-[14px] border border-stone-200 bg-stone-50 px-3 py-2.5 text-stone-700">{typeof paquete.valorTotalFactura === 'number' ? paquete.valorTotalFactura.toFixed(2) : '—'}</div></div>

              {pagoSeleccionado.tipo === 'completo' && (
                <>
                  <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">¿Cómo fue pagado?</span><select value={pagoSeleccionado.medioPago ?? ''} onChange={event => handleMedioPagoChange(event.target.value as TipoPagoParcial)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10">{PARTIAL_PAYMENT_OPTIONS.map(option => <option key={option.value || 'blank'} value={option.value}>{option.label}</option>)}</select></label>
                  {pagoSeleccionado.medioPago === 'transferencia' && (
                    <>
                      <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Número de transferencia</span><input value={pagoSeleccionado.numeroTransferencia ?? ''} onChange={event => handlePagoField('numeroTransferencia', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Referencia bancaria" /></label>
                      <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Banco</span><input value={pagoSeleccionado.banco ?? ''} onChange={event => handlePagoField('banco', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Banco emisor" /></label>
                    </>
                  )}
                  {pagoSeleccionado.medioPago === 'cheque' && (
                    <>
                      <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Número de cheque</span><input value={pagoSeleccionado.numeroCheque ?? ''} onChange={event => handlePagoField('numeroCheque', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Número de cheque" /></label>
                      <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Banco</span><input value={pagoSeleccionado.banco ?? ''} onChange={event => handlePagoField('banco', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Banco emisor" /></label>
                    </>
                  )}
                  {pagoSeleccionado.medioPago === 'nota_credito' && (
                    <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Número nota de crédito</span><input value={pagoSeleccionado.numeroNotaCredito ?? ''} onChange={event => handlePagoField('numeroNotaCredito', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Número de la nota" /></label>
                  )}
                </>
              )}

              {(pagoSeleccionado.tipo === 'efectivo' || pagoSeleccionado.tipo === 'completo') && (
                <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Monto</span><input type="number" min="0" step="0.01" value={pagoSeleccionado.monto ?? ''} onWheel={preventNumberInputWheelChange} onChange={event => handlePagoField('monto', parseOptionalNumber(event.target.value))} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="0.00" /></label>
              )}

              {pagoSeleccionado.tipo === 'transferencia' && (
                <>
                  <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Número de transferencia</span><input value={pagoSeleccionado.numeroTransferencia ?? ''} onChange={event => handlePagoField('numeroTransferencia', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Referencia bancaria" /></label>
                  <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Banco</span><input value={pagoSeleccionado.banco ?? ''} onChange={event => handlePagoField('banco', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Banco emisor" /></label>
                </>
              )}

              {pagoSeleccionado.tipo === 'cheque' && (
                <>
                  <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Número de cheque</span><input value={pagoSeleccionado.numeroCheque ?? ''} onChange={event => handlePagoField('numeroCheque', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Número de cheque" /></label>
                  <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Banco</span><input value={pagoSeleccionado.banco ?? ''} onChange={event => handlePagoField('banco', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Banco emisor" /></label>
                  <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Monto</span><input type="number" min="0" step="0.01" value={pagoSeleccionado.monto ?? ''} onWheel={preventNumberInputWheelChange} onChange={event => handlePagoField('monto', parseOptionalNumber(event.target.value))} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="0.00" /></label>
                </>
              )}

              {pagoSeleccionado.tipo === 'nota_credito' && (
                <>
                  <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Número nota de crédito</span><input value={pagoSeleccionado.numeroNotaCredito ?? ''} onChange={event => handlePagoField('numeroNotaCredito', event.target.value)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="Número de la nota" /></label>
                  <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Valor</span><input type="number" min="0" step="0.01" value={pagoSeleccionado.valorNotaCredito ?? ''} onWheel={preventNumberInputWheelChange} onChange={event => handlePagoField('valorNotaCredito', parseOptionalNumber(event.target.value))} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="0.00" /></label>
                </>
              )}

              {pagoSeleccionado.tipo === 'parcial' && (
                <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Forma de cobro sugerida</span><select value={pagoSeleccionado.tipoPagoParcial ?? ''} onChange={event => handleTipoPagoParcialChange(event.target.value as TipoPagoParcial)} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10">{PARTIAL_PAYMENT_OPTIONS.map(option => <option key={option.value || 'blank'} value={option.value}>{option.label}</option>)}</select><span className="block text-xs text-stone-500">Es una sugerencia: el conductor registra el pago real al finalizar.</span></label>
              )}
              {pagoSeleccionado.tipo === 'parcial' && (
                <label className="space-y-2 text-sm text-stone-700"><span className="font-medium">Monto por cobrar</span><input type="number" min="0" step="0.01" value={montoPorCobrarDe(pagoSeleccionado, paquete.valorTotalFactura) ?? ''} onWheel={preventNumberInputWheelChange} onChange={event => handlePagoField('montoPorCobrar', parseOptionalNumber(event.target.value))} className="w-full rounded-[14px] border border-stone-200 px-3 py-2.5 outline-none transition focus:border-[var(--freisa-red)] focus:ring-4 focus:ring-[var(--freisa-red)]/10" placeholder="0.00" /></label>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50 p-4 text-sm text-stone-700">
            <h4 className="font-semibold text-stone-900">Documento de transferencia</h4>
            <p className="mt-1">
              {initialViaje.transferencia?.numero || 'Sin número'}
              {' · '}
              {initialViaje.transferencia?.bodegaOrigen || '—'} → {initialViaje.transferencia?.bodegaDestino || '—'}
              {' · '}
              {initialViaje.transferencia?.items?.length ?? 0} ítem{(initialViaje.transferencia?.items?.length ?? 0) === 1 ? '' : 's'}
            </p>
            <p className="mt-1 text-xs text-stone-500">Datos leídos del PDF del ERP: no se editan desde aquí.</p>
          </div>

          <div className="space-y-4 rounded-[var(--radius-card)] border border-stone-200 p-4">
            <h4 className="font-semibold text-stone-900">{initialViaje.tipoEntrega === 'envio' ? 'PDF de envío' : 'Guías generadas'}</h4>
            {initialViaje.tipoEntrega === 'envio' && initialViaje.paquete?.envioPdfUrl ? (
              <div className="flex flex-wrap gap-2 text-sm">
                <a href={initialViaje.paquete.envioPdfUrl} target="_blank" rel="noreferrer" className="rounded-full bg-stone-100 px-3 py-1.5 font-medium text-stone-700 transition hover:bg-stone-200">
                  {initialViaje.paquete.envioPdfNombreArchivo || 'PDF de envío'}
                </a>
              </div>
            ) : (initialViaje.paquete?.guiasPdf?.length ?? 0) > 0 ? (
              <div className="flex flex-wrap gap-2 text-sm">
                {initialViaje.paquete?.guiasPdf?.map((guia, index) => (
                  <a key={`${guia.url}-${index}`} href={guia.url} target="_blank" rel="noreferrer" className="rounded-full bg-stone-100 px-3 py-1.5 font-medium text-stone-700 transition hover:bg-stone-200">
                    {guia.nombreArchivo || `Guía ${index + 1}`}
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-500">{initialViaje.tipoEntrega === 'envio' ? 'No hay PDF de envío registrado.' : 'No hay guías generadas registradas.'}</p>
            )}
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 border-t border-stone-200 pt-4">
        <button type="button" onClick={onCancel} className="ui-btn ui-btn-secondary">Cancelar</button>
        <button type="submit" disabled={saving} className="ui-btn ui-btn-primary">{saving ? 'Guardando...' : 'Guardar cambios'}</button>
      </div>
    </form>
    {saving && <DeliveryUpdateLoadingModal />}
    </>
  );
}

function DeliveryUpdateLoadingModal() {
  return (
    <div className="fixed inset-0 z-[2300] flex items-center justify-center bg-stone-950/60 px-4 py-6 backdrop-blur-sm">
      <div
        role="status"
        aria-live="polite"
        className="w-full max-w-md rounded-[var(--radius-card)] border border-white/80 bg-white p-6 text-center shadow-[0_30px_90px_rgba(15,23,42,0.28)]"
      >
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
          <div className="h-7 w-7 animate-spin rounded-full border-4 border-red-100 border-t-[var(--freisa-red)]" />
        </div>
        <h3 className="mt-5 text-xl font-semibold text-stone-950">Estamos actualizando la entrega</h3>
        <p className="mt-2 text-sm text-stone-600">Por favor espera.</p>
      </div>
    </div>
  );
}
